import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { healthcareApi } from "../api/healthcare";
import type {
  UploadedFile,
  LinkedRuleDto,
  DocumentRulesRetaggingTriggerResponse,
  CompliancePipelineStage,
  BatchEvaluationResponse,
} from "../types/healthcare";
import type { PaginatedResponse } from "../types";
import { queryKeys } from "../api/queryKeys";
import {
  invalidateDocuments,
  invalidateProcessingJobs,
} from "../api/queryInvalidation";
import { useTenantId } from "./useTenantId";

/** @deprecated Use `queryKeys.documents` from `../api/queryKeys` instead */
export const documentQueryKeys = queryKeys.documents;

export interface DocumentFilters {
  page?: number;
  pageSize?: number;
  searchText?: string;
  compliancePipelineStages?: CompliancePipelineStage[];
}

/**
 * Hook for fetching document library with pagination and filters
 * Uses React Query for caching and deduplication of API calls
 */
export const useDocumentLibrary = (
  filters: DocumentFilters = {},
  options?: {
    enabled?: boolean;
    refetchOnMount?: boolean;
  },
) => {
  const tenantId = useTenantId();
  const {
    page = 0,
    pageSize = 10,
    searchText,
    compliancePipelineStages,
  } = filters;

  return useQuery({
    queryKey: queryKeys.documents.list(tenantId, filters),
    queryFn: async () => {
      const apiFilters = {
        ...(searchText?.trim() ? { searchText: searchText.trim() } : {}),
        ...(compliancePipelineStages?.length
          ? { compliancePipelineStages }
          : {}),
      };

      const response = await healthcareApi.files.getAllFiles(
        page,
        pageSize,
        Object.keys(apiFilters).length ? apiFilters : undefined,
      );

      if (response.success && response.data) {
        return response.data as PaginatedResponse<UploadedFile>;
      }

      throw new Error("Failed to load files");
    },
    enabled: options?.enabled !== false,
    refetchOnMount: options?.refetchOnMount ?? true,
    refetchOnWindowFocus: false,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook for deleting a document
 */
export const useDeleteDocument = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (fileId: number) => healthcareApi.files.deleteFile(fileId),
    onSuccess: () => {
      invalidateDocuments(queryClient, tenantId);
    },
  });
};

/**
 * Hook for starting document processing
 */
export const useStartProcessing = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (fileId: number) =>
      healthcareApi.processing.startProcessing(fileId),
    onSuccess: () => {
      invalidateDocuments(queryClient, tenantId);
    },
  });
};

/**
 * Hook for fetching rules linked to a document
 * Uses React Query — only fetches when enabled (i.e. when a document is selected)
 */
export const useDocumentLinkedRules = (
  documentId: number | null,
  options?: { enabled?: boolean },
) => {
  const tenantId = useTenantId();

  return useQuery<LinkedRuleDto[]>({
    queryKey: queryKeys.documents.linkedRules(tenantId, documentId ?? 0),
    queryFn: async () => {
      const response = await healthcareApi.files.getDocumentLinkedRules(
        documentId!,
      );
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error("Failed to load linked rules");
    },
    enabled:
      options?.enabled !== false && documentId !== null && documentId > 0,
    staleTime: 60000,
  });
};

export const useSingleDocumentComplianceEvaluation = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: async (
      documentId: number,
    ): Promise<BatchEvaluationResponse> => {
      const response =
        await healthcareApi.files.triggerSingleDocumentComplianceEvaluation(
          documentId,
        );
      if (response.success && response.data) {
        return response.data;
      }
      const errorResponse = response as { errors?: string[]; message?: string };
      throw new Error(
        errorResponse.errors?.join(", ") ||
          errorResponse.message ||
          "Failed to trigger compliance evaluation",
      );
    },
    onSuccess: () => {
      invalidateProcessingJobs(queryClient, tenantId);
    },
  });
};

export const useRetriggerDocumentRulesTagging = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: async (
      documentId: number,
    ): Promise<DocumentRulesRetaggingTriggerResponse> => {
      const response =
        await healthcareApi.files.retriggerDocumentRulesTagging(documentId);
      if (response.success && response.data) {
        return response.data;
      }
      const errorResponse = response as {
        errors?: string[];
        message?: string;
      };
      throw new Error(
        errorResponse.errors?.join(", ") ||
          errorResponse.message ||
          "Failed to retrigger rules tagging",
      );
    },
    onSuccess: () => {
      invalidateDocuments(queryClient, tenantId);
      invalidateProcessingJobs(queryClient, tenantId);
    },
  });
};
